import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import {
  Laptop, Wifi, LifeBuoy, Ticket, Activity, ShieldCheck, Signal, Bell,
  Download, Check, Apple, MonitorSmartphone, Zap, ArrowRight
} from 'lucide-react';

// Página de releases con los instaladores para Mac y Windows
const DOWNLOAD_URLS = {
  mac: 'https://github.com/CloudBaseLatam/Cloudbase_Endpoint/releases',
  windows: 'https://github.com/CloudBaseLatam/Cloudbase_Endpoint/releases'
};

const features = [
  {
    icon: Wifi,
    title: 'Monitoreo WiFi en tiempo real',
    description:
      'Detecta latencia, pérdida de paquetes, canales saturados y calidad de señal en cada endpoint del cliente.'
  },
  {
    icon: LifeBuoy,
    title: 'Soporte remoto asistido',
    description:
      'Diagnóstico y acciones remotas desde tu mesa de ayuda: reinicios, tests de red, captura de logs, sin llamadas confusas.'
  },
  {
    icon: Ticket,
    title: 'Tickets desde el endpoint',
    description:
      'El usuario final abre un ticket con un clic desde el agente instalado; llega con la info del equipo y la red pre-adjunta.'
  },
  {
    icon: Activity,
    title: 'Métricas de red continuas',
    description:
      'Historial de conexión, saltos de red, IP pública, gateway y calidad DNS para diagnóstico rápido.'
  },
  {
    icon: ShieldCheck,
    title: 'Seguro y liviano',
    description:
      'Agente firmado, telemetría cifrada en tránsito y consumo mínimo de CPU/RAM. Diseñado para correr todo el día sin molestar.'
  },
  {
    icon: Bell,
    title: 'Alertas proactivas',
    description:
      'Notificaciones automáticas cuando un endpoint se cae, cambia de red o presenta problemas recurrentes.'
  }
];

const steps = [
  {
    title: 'Descarga e instala',
    description:
      'Un solo instalador para Mac (.dmg) o Windows (.exe). Se emparejará con tu cuenta Cloudbase automáticamente.'
  },
  {
    title: 'Conecta al usuario',
    description:
      'El endpoint reporta estado de red y equipo a tu consola en tiempo real. Sin configuración adicional.'
  },
  {
    title: 'Resuelve más rápido',
    description:
      'El usuario abre tickets con un clic; tu equipo diagnostica y responde con toda la data del incidente.'
  }
];

const systemRequirements = {
  mac: ['macOS 11 (Big Sur) o superior', 'Procesadores Intel y Apple Silicon (M1/M2/M3)', '80 MB de espacio en disco'],
  windows: ['Windows 10 y Windows 11 (64-bit)', 'Windows Server 2019 y 2022', '80 MB de espacio en disco']
};

export default function EndpointPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const endpointSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Cloudbase Endpoint',
    operatingSystem: 'macOS, Windows',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Help Desk Software, Network Monitoring',
    description:
      'Agente ligero para Mac y Windows que monitorea la calidad de red WiFi del usuario final y permite abrir tickets de soporte técnico con un clic. Diseñado para mesas de ayuda modernas en Latinoamérica y España.',
    url: 'https://cloudbase.cl/cloudbase-endpoint',
    downloadUrl: 'https://github.com/CloudBaseLatam/Cloudbase_Endpoint/releases',
    inLanguage: 'es',
    provider: {
      '@type': 'Organization',
      name: 'Cloudbase',
      url: 'https://cloudbase.cl'
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: '39.00'
    }
  };

  return (
    <>
      <SEOHead
        title="Cloudbase Endpoint - Soporte y Monitoreo WiFi Mac y Windows | Cloudbase"
        description="Cloudbase Endpoint: agente para Mac y Windows que monitorea la red WiFi del usuario final y permite abrir tickets de soporte técnico con un clic."
        keywords="cloudbase endpoint, soporte técnico remoto, monitoreo WiFi, agente Mac, agente Windows, helpdesk, tickets endpoint, gestión de red usuarios finales"
        path="/cloudbase-endpoint"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Productos', item: '/#modulos' },
          { name: 'Cloudbase Endpoint', item: '/cloudbase-endpoint' }
        ]}
        extraSchemas={[endpointSchema]}
      />
      <div className="min-h-screen bg-white" data-testid="endpoint-page">
        <Header />

        {/* Hero */}
        <section className="pt-32 pb-24 bg-black relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90"></div>
          <div className="absolute top-40 -left-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-20 pointer-events-none"></div>
          <div className="absolute bottom-10 -right-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl opacity-20 pointer-events-none"></div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-blue-600/20 border border-blue-600/30 text-blue-300 rounded-full text-sm font-semibold">
                <Laptop className="w-4 h-4" />
                Cloudbase Endpoint
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Soporte y monitoreo WiFi en cada endpoint de tu cliente
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                El agente ligero para <strong className="text-white font-semibold">Mac y Windows</strong> que
                monitorea la calidad de red del usuario final y le permite abrir tickets de soporte con un clic.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
                <Button
                  size="lg"
                  className="bg-white hover:bg-gray-100 text-black px-8 py-7 text-base rounded font-semibold transition-all duration-200 flex items-center gap-3"
                  onClick={() => window.open(DOWNLOAD_URLS.mac, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-mac"
                >
                  <Apple className="w-6 h-6" />
                  Descargar para Mac
                </Button>
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-base rounded font-semibold transition-all duration-200 flex items-center gap-3"
                  onClick={() => window.open(DOWNLOAD_URLS.windows, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-windows"
                >
                  <MonitorSmartphone className="w-6 h-6" />
                  Descargar para Windows
                </Button>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Instalación en 60 segundos</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Agente firmado y seguro</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Sin impacto en el rendimiento</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Compatible con Intel, ARM y Apple Silicon</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-32 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
                Todo lo que necesitas en el endpoint
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                Visibilidad de la red del usuario final y una vía directa a soporte, en una sola app.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={i}
                    className="bg-white border border-gray-200 p-8 rounded-lg hover:shadow-lg transition-shadow"
                    data-testid={`endpoint-feature-${i}`}
                  >
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{f.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">Cómo Funciona</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                De la descarga al primer diagnóstico en menos de dos minutos.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
              {steps.map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    {i + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* System requirements */}
        <section className="py-32 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-gray-900">
                Requisitos del sistema
              </h2>
              <p className="text-lg text-gray-600 font-light">
                Cloudbase Endpoint es liviano y compatible con la mayoría de equipos modernos.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Mac */}
              <div className="bg-white border border-gray-200 p-10 rounded-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-black rounded-lg flex items-center justify-center">
                    <Apple className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider">Para</div>
                    <div className="text-2xl font-bold text-gray-900">macOS</div>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {systemRequirements.mac.map((req, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  className="w-full bg-black hover:bg-gray-800 text-white py-6 text-base rounded font-semibold flex items-center justify-center gap-2"
                  onClick={() => window.open(DOWNLOAD_URLS.mac, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-mac-req"
                >
                  <Download className="w-5 h-5" />
                  Descargar para Mac
                </Button>
              </div>

              {/* Windows */}
              <div className="bg-white border border-gray-200 p-10 rounded-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center">
                    <MonitorSmartphone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wider">Para</div>
                    <div className="text-2xl font-bold text-gray-900">Windows</div>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {systemRequirements.windows.map((req, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base rounded font-semibold flex items-center justify-center gap-2"
                  onClick={() => window.open(DOWNLOAD_URLS.windows, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-windows-req"
                >
                  <Download className="w-5 h-5" />
                  Descargar para Windows
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Value props */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Signal className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">100%</div>
                  <div className="text-gray-600">Visibilidad de la red del usuario</div>
                </div>
                <div>
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-7 h-7 text-green-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">-60%</div>
                  <div className="text-gray-600">Tiempo promedio de diagnóstico</div>
                </div>
                <div>
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Ticket className="w-7 h-7 text-purple-600" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2">1 clic</div>
                  <div className="text-gray-600">Para abrir un ticket con contexto</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
                Empieza a monitorear tus endpoints hoy
              </h2>
              <p className="text-xl text-gray-300 mb-12 font-light">
                Instala Cloudbase Endpoint en minutos y dale a tu mesa de ayuda visibilidad total sobre la red de cada usuario.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  size="lg"
                  className="bg-white hover:bg-gray-100 text-black px-8 py-7 text-base rounded font-semibold flex items-center gap-3"
                  onClick={() => window.open(DOWNLOAD_URLS.mac, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-mac-footer"
                >
                  <Apple className="w-6 h-6" />
                  Descargar para Mac
                </Button>
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-base rounded font-semibold flex items-center gap-3"
                  onClick={() => window.open(DOWNLOAD_URLS.windows, '_blank', 'noopener,noreferrer')}
                  data-testid="endpoint-download-windows-footer"
                >
                  <MonitorSmartphone className="w-6 h-6" />
                  Descargar para Windows
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-7 text-base rounded font-semibold flex items-center gap-2"
                  onClick={() => (window.location.href = '/demo')}
                  data-testid="endpoint-demo-cta"
                >
                  Solicitar Demo
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
